import { useState, type ReactNode } from "react";
import { parseISO } from "date-fns";
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Mail,
  MessageSquare,
  Paperclip,
  Phone,
  PhoneMissed,
  PhoneOff,
  User,
  Voicemail,
} from "lucide-react";

import { TranscriptView } from "@/components/transcript-view";
import { formatPhoneNumber } from "@/lib";
import { formatBusinessDateTime, formatBusinessTime } from "@/lib/business-date";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { WentToSpamBadge } from "@/components/records/went-to-spam-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ClampedText } from "@/components/clamped-text";
import { linkifyText } from "@/lib/linkify";

import { useContactDetailEmail, useContactDetailTranscript } from "./hooks";
import type {
  ContactAttachment,
  ContactAttachmentMedia,
  ContactHistoryItem,
  DetailEndpoints,
} from "./types";

// Exported so the deal timeline's call adapter (activity-timeline/) can reuse the
// exact same outcome label / icon / attribution rules instead of restating them —
// pure functions, no behavior change to ContactRow below.
export function channelIcon(item: ContactHistoryItem) {
  if (item.is_voicemail || item.call_outcome === "voicemail") return Voicemail;
  if (item.call_outcome === "missed" || item.call_outcome === "no_answer")
    return PhoneMissed;
  if (item.call_outcome === "no_contact") return PhoneOff;
  if (item.channel === "call") return Phone;
  if (item.channel === "email") return Mail;
  if (item.channel === "sms") return MessageSquare;
  return User;
}

/** Header label for the row — reads the call's outcome (answered / missed / no
 * answer / voicemail) so a call isn't an opaque "Call" with an empty transcript.
 * Falls back to the channel for non-call touches (email / sms / manual). */
export function channelLabel(item: ContactHistoryItem): string {
  switch (item.call_outcome) {
    case "voicemail":
      return "Voicemail";
    case "answered":
      return "Answered call";
    case "answered_unattributed":
      return "Answered call (shared line)";
    case "answered_unverified":
      return "Answered call, no transcript";
    case "no_contact":
      return "Answered call, no conversation";
    case "missed":
      return "Missed call";
    case "no_answer":
      return "Call — no answer";
  }
  if (item.is_voicemail) return "Voicemail";
  if (item.channel === "call") return "Call";
  if (item.channel === "email") return "Email";
  if (item.channel === "sms") return "SMS";
  return item.channel.charAt(0).toUpperCase() + item.channel.slice(1);
}

/** Picture or paperclip. The words come from the server (`attachment_summary`)
 * so this and the CCC worklist row can't pluralise differently; only the glyph
 * is decided here, and only when EVERY attachment is an image — a mixed set
 * gets the neutral clip rather than a photo icon that would misdescribe it. */
export function attachmentIcon(attachments: ContactAttachment[] = []) {
  const allImages =
    attachments.length > 0 && attachments.every((a) => a.content_type?.startsWith("image/"));
  return allImages ? ImageIcon : Paperclip;
}

/** The photograph itself, under the "1 photo" line that counts it.
 *
 * Opens full-size in a NEW TAB rather than a Dialog: this timeline renders inside
 * a Sheet on two of its three surfaces (the CCC detail sheet and the LCC
 * contact-history sheet), and nested Radix overlays are a known stacking hazard
 * here (`ui/overlay-stacking.test.tsx`). A tab also gives the browser's own zoom,
 * rotate and save for free — most of what someone wants from a customer's photo
 * of a rusted heat exchanger.
 *
 * Non-images (~13% of the corpus: PDFs, videos, vCards) get a named link rather
 * than a broken `<img>`. The label falls back to the content type because
 * RingCentral does not always send a file name. */
export function AttachmentMedia({ media }: { media?: ContactAttachmentMedia[] }) {
  if (!media?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="contact-attachment-media">
      {media.map((m, i) =>
        m.content_type?.startsWith("image/") ? (
          <a
            key={m.url}
            href={m.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block overflow-hidden rounded-md border transition-opacity hover:opacity-80"
          >
            {/* The customer's own file name where there is one. Never invented copy:
                alt text describing the picture would be a guess, and the line above
                already says what it is. Lazy because a heavy customer page carries
                dozens of these (65 on the worst one measured) at up to ~1.3 MB each. */}
            <img
              src={m.url}
              alt={m.file_name ?? "Attachment"}
              loading="lazy"
              className="h-24 w-24 object-cover"
            />
          </a>
        ) : (
          <a
            key={m.url}
            href={m.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
          >
            <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
            {m.file_name ?? m.content_type ?? `Attachment ${i + 1}`}
          </a>
        ),
      )}
    </div>
  );
}

function sourceLabel(s: string): string {
  if (s === "ringcentral") return "RingCentral";
  if (s === "gmail") return "Gmail";
  if (s === "manual") return "Manual";
  return s;
}

/** Our side of the call: who worked it, and — inbound — the line the customer
 * reached. Returns null when we know neither, so the row simply doesn't grow a
 * second line rather than rendering an empty one.
 *
 * The two halves are independent on purpose. A missed call has no handler but
 * its line is the entire point ("rang Adam Rothman's direct number" vs "died in
 * the after-hours IVR"); an outbound call has a handler and no meaningful line
 * (the number dialled is the customer's own, which the row already implies).
 *
 * The line is dropped when it merely repeats the handler — a direct dial that
 * the same person then answered would otherwise read "Handled by Adam Rothman ·
 * via Adam Rothman". */
export function attributionLine(item: ContactHistoryItem): string | null {
  const { handled_by, line_name, line_is_direct } = item;
  const inbound = item.direction === "inbound";
  if (handled_by) {
    const verb = inbound ? "Handled by" : "Called by";
    const via = line_name && line_name !== handled_by ? ` · via ${line_name}` : "";
    return `${verb} ${handled_by}${via}`;
  }
  if (!line_name) return null;
  // No handler: the line stands alone. `line_is_direct` is tri-state — only an
  // explicit true earns the stronger "Direct line to" phrasing, because null
  // means "the extension isn't in the directory", not "it wasn't direct".
  return line_is_direct === true ? `Direct line to ${line_name}` : line_name;
}

/** A call's transcript detail: the status ladder (loading, error, missed / no
 * answer, pending, failed), then summary, sentiment and the bubbles. Shared by
 * `ContactRow` (transcript folded behind its own toggle) and the deal timeline's
 * call card (`inlineTranscript`: the card's chevron already is the toggle), so
 * the wording and status rules never drift between the two. Fetches on mount —
 * callers mount it only once the row is open. */
export function CallTranscriptDetail({
  detailEndpoints,
  item,
  inlineTranscript = false,
  summaryShown,
}: {
  detailEndpoints: DetailEndpoints;
  item: ContactHistoryItem;
  inlineTranscript?: boolean;
  /** The part of the summary the caller already shows (the deal timeline card's headline and
   *  preview): it is left out here, and only the rest, if any, is shown. */
  summaryShown?: string | null;
}) {
  const { transcriptUrl } = detailEndpoints(item.id);
  const transcript = useContactDetailTranscript(transcriptUrl, { enabled: true });
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  // Voicemails land as detail_kind "call" but read better as "voicemail".
  const noun = item.is_voicemail ? "voicemail" : "call";
  if (transcript.isLoading) return <Skeleton className="h-16 w-full" />;
  if (transcript.isError)
    return <p className="text-xs text-destructive">Couldn’t load the transcript.</p>;
  const t = transcript.data;
  if (!t || t.status === "unavailable") {
    // A missed / unconnected call has no recording by nature — say so, rather
    // than imply a transcript is missing or still coming.
    if (item.call_outcome === "missed")
      return (
        <p className="text-xs text-muted-foreground">
          Missed call — no voicemail, so there’s nothing to transcribe.
        </p>
      );
    if (item.call_outcome === "no_answer")
      return (
        <p className="text-xs text-muted-foreground">
          No answer — the call didn’t connect, so there’s nothing to transcribe.
        </p>
      );
    return <p className="text-xs text-muted-foreground">No transcript for this {noun}.</p>;
  }
  if (t.status === "pending")
    return <p className="text-xs text-muted-foreground">Transcription in progress — check back shortly.</p>;
  if (t.status === "failed" || t.status === "skipped")
    return <p className="text-xs text-muted-foreground">Transcript unavailable for this {noun}.</p>;
  const shown = summaryShown?.trim();
  const summary = shown && t.summary?.trim().startsWith(shown) ? t.summary.trim().slice(shown.length).trim() : t.summary;
  return (
    <div className="space-y-2">
      {summary ? (
        <p className="text-xs italic text-muted-foreground">{summary}</p>
      ) : shown ? null : (
        <p className="text-xs italic text-muted-foreground">
          No summary available for this {noun}.
        </p>
      )}
      {t.q8_sentiment ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">Sentiment:</span>{" "}
          <span className="capitalize">{t.q8_sentiment}</span>
          {t.q9_sentiment_description ? ` — ${t.q9_sentiment_description}` : ""}
        </p>
      ) : null}
      {inlineTranscript ? (
        <TranscriptView transcript={t.transcript} moments={t.moments ?? []} />
      ) : (
        <Collapsible open={transcriptOpen} onOpenChange={setTranscriptOpen}>
          <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            {transcriptOpen ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            {transcriptOpen ? "Hide full transcript" : "Expand for full transcript"}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            {transcriptOpen ? (
              <TranscriptView transcript={t.transcript} moments={t.moments ?? []} />
            ) : null}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

/** One email's body: subject, from → to, the body, and the quoted history
 * folded behind "Show earlier messages". Shared by `ContactRow` and the deal
 * timeline's Gmail-style thread. `action` (e.g. the thread's per-message ⋯)
 * sits beside the subject when given. Fetches on mount. */
/** An email as a rep reads it on the deal card: runs of blank lines collapse to one, and the signature joins the
 *  quoted thread behind "Show quoted text". The body's clamp is `ClampedText`'s. */
export function foldEmail(body: string | null | undefined, quoted: string | null | undefined) {
  const clean = (body ?? "").replace(/\r/g, "").replace(/\n[ \t]*(?:\n[ \t]*)+\n/g, "\n\n").trim();
  const sig = clean.search(/\n--[ \t]*\n/);
  const main = sig >= 0 ? clean.slice(0, sig).trim() : clean;
  const signature = sig >= 0 ? clean.slice(sig).trim() : "";
  return { main, quoted: [signature, quoted ?? ""].filter(Boolean).join("\n\n") || null };
}

export function EmailDetail({
  detailEndpoints,
  item,
  action,
  bodyOnly = false,
  showAddresses = false,
  fallback,
}: {
  detailEndpoints: DetailEndpoints;
  item: ContactHistoryItem;
  action?: ReactNode;
  /** The caller's header already names the subject and who → who (the deal timeline card):
   *  show the body alone. */
  bodyOnly?: boolean;
  /** With `bodyOnly`, still show from → to above the body (an older message whose own row it replaces). */
  showAddresses?: boolean;
  /** Words the caller already had (the stored snippet), shown when the body can't be read. */
  fallback?: string | null;
}) {
  const { emailUrl } = detailEndpoints(item.id);
  const emailQ = useContactDetailEmail(emailUrl, { enabled: true });
  const [historyOpen, setHistoryOpen] = useState(false);
  // A body that can't load still keeps its action (e.g. moving this one email alone).
  const withAction = (status: ReactNode) =>
    action ? (
      <div className="flex items-start justify-between gap-2">
        {status}
        {action}
      </div>
    ) : (
      status
    );
  if (emailQ.isLoading) return <Skeleton className="h-16 w-full" />;
  const fallbackWords = fallback ? (
    <p className="min-w-0 whitespace-pre-wrap break-words text-sm text-foreground">{linkifyText(fallback)}</p>
  ) : null;
  if (emailQ.isError)
    return withAction(fallbackWords ?? <p className="text-xs text-destructive">Couldn’t load the email.</p>);
  const e = emailQ.data;
  if (!e || e.status !== "ok")
    return withAction(fallbackWords ?? <p className="text-xs text-muted-foreground">Email body unavailable.</p>);
  const subject = e.subject ? <p className="text-sm font-medium">{e.subject}</p> : null;
  // The deal timeline's card (`bodyOnly`) folds the email; the full view keeps the body as Gmail sent it.
  const folded = bodyOnly ? foldEmail(e.body, e.quoted_history) : null;
  const quoted = folded ? folded.quoted : e.quoted_history;
  return (
    <div className="space-y-1">
      {bodyOnly ? (
        <>
          {showAddresses ? (
            <p className="break-all text-xs text-muted-foreground">
              {e.from_address} → {e.to_addresses}
            </p>
          ) : null}
          <div className="flex items-start justify-between gap-2">
            {/* Clamped at 8 lines; "Show more" appears only when the body really overflows. The wrapper keeps the
                toggle under the body, with the row's action beside both. */}
            <div className="min-w-0 flex-1">
              <ClampedText
                className="min-w-0 whitespace-pre-wrap break-words text-[15px] leading-snug text-foreground"
                clampClassName="line-clamp-[8]"
                buttonClassName="mt-1 text-[13px]"
                moreLabel="Show more"
                lessLabel="Show less"
              >
                {folded?.main ? linkifyText(folded.main) : "(empty body)"}
              </ClampedText>
            </div>
            {action}
          </div>
        </>
      ) : (
        <>
          {action ? (
            <div className="flex items-start justify-between gap-2">
              {subject ?? <span />}
              {action}
            </div>
          ) : (
            subject
          )}
          <p className="text-xs text-muted-foreground">
            {e.from_address} → {e.to_addresses}
          </p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">
            {e.body ? linkifyText(e.body) : "(empty body)"}
          </p>
        </>
      )}
      {/* The thread quoted under the message (and, on the deal card, the signature) mostly repeats rows this
          timeline already lists, so it folds behind a plain "Show quoted text". */}
      {quoted ? (
        <Collapsible open={historyOpen} onOpenChange={setHistoryOpen} className="mt-2">
          <CollapsibleTrigger className="text-[13px] font-medium text-muted-foreground hover:text-foreground hover:underline">
            {historyOpen ? "Hide quoted text" : "Show quoted text"}
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            {historyOpen ? (
              <p
                className="whitespace-pre-wrap border-l-2 border-border pl-3 text-xs text-muted-foreground"
                data-testid="email-quoted-history"
              >
                {quoted}
              </p>
            ) : null}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}

/** Lazy detail body for a call or email entry — only mounts (and only then
 * fetches) when the row is expanded. The transcript / email endpoints are built
 * by the caller's `detailEndpoints` so the same body serves lead- and
 * person-scoped surfaces. */
function ContactDetail({
  detailEndpoints,
  item,
}: {
  detailEndpoints: DetailEndpoints;
  item: ContactHistoryItem;
}) {
  if (item.detail_kind === "call") return <CallTranscriptDetail detailEndpoints={detailEndpoints} item={item} />;
  if (item.detail_kind === "email") return <EmailDetail detailEndpoints={detailEndpoints} item={item} />;
  return null;
}

/** "On another deal · <deal>" / "Not on any open deal" — the words a touch that
 * isn't this deal's carries. Shared by `ContactRow`'s banner and the deal
 * timeline's activity cards. Renders nothing for a touch on this deal. */
export function DealElsewhereLabel({ dealView }: { dealView: ContactDealView }) {
  if (dealView.link === "this") return null;
  if (dealView.link === "none") return <>Not on any open deal</>;
  return (
    <>
      On another deal ·{" "}
      {dealView.onOpenDeal ? (
        <button
          type="button"
          onClick={dealView.onOpenDeal}
          className="text-left font-medium text-foreground underline underline-offset-2"
        >
          {dealView.otherDealName || "Untitled deal"}
        </button>
      ) : (
        <span className="font-medium text-foreground">{dealView.otherDealName || "Untitled deal"}</span>
      )}
    </>
  );
}

/** One touch. Exported so the deal timeline can interleave calls, emails and
 * texts with the deal's other events using this exact row — transcript and
 * email expansion included. `brandLabel` names a sibling brand when a touch was
 * logged under one (a Metro-Aire call on a Bell deal); the caller decides when
 * that is worth saying, this row only prints it. */
/** The deal panel's view of a contact: how it relates to the deal being viewed, and what a person can do about it.
 *  Without it a row renders as it always has (the customer page, the LCC sheet). */
export interface ContactDealView {
  link: "this" | "other" | "none";
  /** Names the other deal, cleaned of markup. */
  otherDealName?: string | null;
  /** Opens the other deal in the same panel. */
  onOpenDeal?: (() => void) | null;
  /** "Belongs to This Deal"; absent for someone who may not move it. */
  onRelink?: (() => void) | null;
}

export function ContactRow({
  detailEndpoints,
  item,
  brandLabel,
  dealView,
}: {
  detailEndpoints: DetailEndpoints;
  item: ContactHistoryItem;
  brandLabel?: string | null;
  dealView?: ContactDealView;
}) {
  const [open, setOpen] = useState(false);
  const Icon = channelIcon(item);
  const Attachment = attachmentIcon(item.attachments);
  const Dir = item.direction === "inbound" ? ArrowDownLeft : ArrowUpRight;
  const when = parseISO(item.contact_at);
  const expandable = item.detail_kind !== "none";
  // A touch attributed to an earlier/sibling opportunity — shown for context
  // but dimmed, with a "re: <deal>" tag so it's clearly not this lead's work.
  //
  // An ATTRIBUTED deal is what makes it "other", not merely "not the current
  // one". `is_current_lead` is false for every row on the person-scoped
  // customer page (there is no lead in hand there), so keying on it alone
  // captioned a touch with no deal at all — an inbound text from someone we
  // have no opportunity for — "re: another deal", naming a deal that does not
  // exist. `attributed_lead_id` is the honest discriminator: null means there
  // was nothing to attribute to. The TITLE is not, because a real sibling deal
  // can be untitled, and that row should still say it belongs elsewhere.
  const isOther = !item.is_current_lead && !!item.attributed_lead_id;
  const attribution = attributionLine(item);
  // On the deal panel a touch that is not this deal's says so in words on its own line, not by fading alone:
  // two people read another job's history as this deal's when the only sign was a faint "re:" tag.
  const elsewhere = dealView && dealView.link !== "this" ? dealView : null;

  return (
    <div
      className={
        elsewhere
          ? "rounded-md border border-dashed border-border bg-muted/50 px-3 py-2 text-muted-foreground"
          : `rounded-md border border-border/60 px-3 py-2 ${isOther ? "opacity-70" : ""}`
      }
      data-deal-link={dealView?.link}
    >
      {elsewhere ? (
        <div className="-mx-3 -mt-2 mb-2 flex items-center justify-between gap-2 border-b border-dashed border-border px-3 py-1.5 text-xs">
          <span className="min-w-0 break-words" data-testid="contact-deal-label">
            <DealElsewhereLabel dealView={elsewhere} />
          </span>
          {elsewhere.onRelink ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 px-2 text-xs max-md:h-9"
              onClick={elsewhere.onRelink}
            >
              Belongs to This Deal
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
            <span className="font-medium">{channelLabel(item)}</span>
            <Dir className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground capitalize">{item.direction}</span>
            <span className="text-xs text-muted-foreground">· {sourceLabel(item.source_system)}</span>
            {item.went_to_spam ? <WentToSpamBadge /> : null}
            {brandLabel ? (
              <span
                className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                data-testid="contact-brand"
                title="Logged under a sibling brand of this customer"
              >
                {brandLabel}
              </span>
            ) : null}
            {isOther && !dealView ? (
              <span
                className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                title="Logged against another of this customer's opportunities"
              >
                re: {item.attributed_lead_title ?? "another deal"}
              </span>
            ) : null}
          </div>
          {/* The time of day, like every other card in the timeline: the business-day
              heading above the row carries the date, and the full date and time are a
              hover away. A relative "4 months ago" under a dated heading said less. */}
          <p className="text-xs text-muted-foreground" title={formatBusinessDateTime(when)}>
            {formatBusinessTime(when)}
          </p>
          {attribution ? (
            <p
              className="text-xs text-muted-foreground"
              data-testid="call-attribution"
              title={formatPhoneNumber(item.line_number) ?? undefined}
            >
              {attribution}
            </p>
          ) : null}
          {item.subject ? (
            <p className="mt-1 text-sm">{item.subject}</p>
          ) : null}
          {item.note ? (
            <p className="mt-1 whitespace-pre-wrap rounded bg-muted/50 px-2 py-1 text-sm">
              {item.note}
            </p>
          ) : null}
          {/* A text can carry a photo alongside its words, or INSTEAD of them —
              an MMS with no body at all, which used to render as a bare channel
              and timestamp. Sits under the message when there is one, and is
              the row's whole content when there isn't. */}
          {item.attachment_summary ? (
            <p
              className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"
              data-testid="contact-attachments"
            >
              <Attachment className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {item.attachment_summary}
            </p>
          ) : null}
          <AttachmentMedia media={item.attachment_media} />
          {item.channel_other ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {item.channel_other}
            </p>
          ) : null}

          {expandable ? (
            <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
              <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                {open ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                {item.detail_kind === "call"
                  ? "View transcript/summary"
                  : "View email"}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                {open ? <ContactDetail detailEndpoints={detailEndpoints} item={item} /> : null}
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </div>
      </div>
    </div>
  );
}
